/**
 * 🔴 ONE ICON SET, ONE GRID, ONE STROKE — design-system DS-11b3 (§1.7; F477, F581, and F65's insight icon).
 *
 * The shell drew thirteen hand-made paths (`NAV_ICONS`) through a private `Icon` in `routes.tsx` at a 1.7 stroke; this
 * is the one component every app draws an icon with. The geometry is LUCIDE's (ISC; the notice is
 * `web/src/assets/licences/lucide.txt`, and the build carries it to `assets/licences/`, font-licences.ts), copied as
 * path data rather than taken as a dependency: `lucide-react` would bring its own component and `createLucideIcon` into
 * a first load that counts bytes (plan §0.3 item 6). Each icon is ONE `d` string — Lucide's `circle`, `rect`, `line`
 * and `polyline` written as path commands, with Lucide's own numbers (icon.spec.tsx holds every one to its grid).
 *
 * The Lucide grid: a 24 box, a 2 stroke, round caps and joins — set by `.wb-icon` in components.css rather than on
 * every element, so the attributes are not repeated in the bundle. Sizes are the `--icon-*` tokens: `md` (20px, the
 * default), `sm` (16px), `lg` (24px).
 *
 * Decorative unless `label` is given: an icon beside its words is `aria-hidden` (the words are the name), and an icon
 * that stands alone is `role="img"` with the label as its name.
 *
 * ⚠️ ADDING ONE: take the icon from lucide.dev, convert each element to path commands with Lucide's own numbers, and
 * name it as `lucide-react` names it (camelCase). A shape drawn here by hand is the mixed set F477 found.
 */
/** The page outline Lucide's `file-*` icons share, written once: the two below differ only in what is on the page. */
const PAGE = 'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2zM14 2v5a1 1 0 0 0 1 1h5';

export const ICONS = {
  // Lucide `house`
  house: 'M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  // Lucide `file-text`
  fileText: `${PAGE}M10 9H8M16 13H8M16 17H8`,
  // Lucide `shopping-cart`
  shoppingCart:
    'M2.05 2.05l1.099-.028a1 1 0 0 1 1.008.815l2.69 14.347A1 1 0 0 0 7.83 18H18M4.563 5h16.435a1 1 0 0 1 .981 1.204l-1.026 6.226A2 2 0 0 1 18.962 14H6.25M16 20a2 2 0 1 0 4 0a2 2 0 1 0-4 0M6 20a2 2 0 1 0 4 0a2 2 0 1 0-4 0',
  // Lucide `package`
  package: 'M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73zM12 22V12M3.29 7L12 12 20.71 7M7.5 4.27l9 5.15',
  // Lucide `credit-card`
  creditCard: 'M4 5h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-16a2 2 0 0 1-2-2v-10a2 2 0 0 1 2-2zM2 10h20M6 14h2',
  // Lucide `book-text`
  bookText: 'M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20M8 11h8M8 7h6',
  // Lucide `briefcase`
  briefcase: 'M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16M4 6h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-16a2 2 0 0 1-2-2v-10a2 2 0 0 1 2-2z',
  // Lucide `users`
  users: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M16 3.128a4 4 0 0 1 0 7.744M22 21v-2a4 4 0 0 0-3-3.87M5 7a4 4 0 1 0 8 0a4 4 0 1 0-8 0',
  // Lucide `file-chart-column`: the reports (F65 — the bare bar chart read as "statistics", not as a page you read)
  fileChartColumn: `${PAGE}M8 18v-1M12 18v-6M16 18v-3`,
  // Lucide `search`
  search: 'M21 21l-4.34-4.34M3 11a8 8 0 1 0 16 0a8 8 0 1 0-16 0',
  // Lucide `menu`
  menu: 'M4 5h16M4 12h16M4 19h16',
  // Lucide `chevron-left`
  chevronLeft: 'M15 18l-6-6 6-6',
  // Lucide `chevron-right`
  chevronRight: 'M9 18l6-6-6-6',
  // Lucide `x`
  x: 'M18 6 6 18M6 6l12 12',
} as const;

export type IconName = keyof typeof ICONS;
export type IconSize = 'sm' | 'md' | 'lg';

export function Icon({ name, size = 'md', label, className }: { name: IconName; size?: IconSize; label?: string; className?: string }) {
  return (
    <svg
      className={['wb-icon', size === 'md' ? '' : `wb-icon--${size}`, className].filter(Boolean).join(' ')}
      viewBox="0 0 24 24"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      <path d={ICONS[name]} />
    </svg>
  );
}
